import { useMemo } from 'react';
import { Alert, Box, colors, Grid, IconButton, Switch, Typography } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import TextFieldsOutlinedIcon from '@mui/icons-material/TextFieldsOutlined';
import VideocamOutlinedIcon from '@mui/icons-material/VideocamOutlined';
import { Button } from '../../../../components/ui';
import FileInput from '../../../../components/Shared/FileUploadPreview/FileInput.jsx';
import OrganisationBotFields from './OrganisationBotFields';
import useTemplateForm from '../../hooks/useTemplateForm';
import useTemplateSx from '../../hooks/useTemplateSx';
import {
  CategoryOptions,
  HeaderOptions,
  LanguageList,
} from '../../utils/templateConstants';
import { getHeaderAcceptValue } from '../../utils/templateLimitUtils';
import TemplateSearchableSelect from '../shared/TemplateSearchableSelect';
import TemplateTagsSelect from '../shared/TemplateTagsSelect';
import Field from '../ui/Field';
import FormSection from '../ui/FormSection';
import OptionChipGroup from '../ui/OptionChipGroup';
import RequiredLabel from '../ui/RequiredLabel';
import TemplateButtonInputs from './TemplateButtonInputs';
import TemplateClickTrackingField from './TemplateClickTrackingField';
import TemplateMessagePreview from './TemplateMessagePreview';
import TemplateRejectionAlert from '../ui/TemplateRejectionAlert';
import WhatsAppBodyEditor from '../../../../components/Whatsapp/TemplateV1/Template/WhatsAppBodyEditor.tsx';
import { typography } from '../../../../theme/tokens/typography.js';
import { spacing } from '../../../../theme/tokens/spacing.js';


const HEADER_TYPE_OPTIONS = HeaderOptions.map((option) => ({
  ...option,
  icon:
    {
      TEXT: TextFieldsOutlinedIcon,
      IMAGE: ImageOutlinedIcon,
      VIDEO: VideocamOutlinedIcon,
      DOCUMENT: DescriptionOutlinedIcon,
    }[option.value] ?? TextFieldsOutlinedIcon,
}));

export default function TemplateForm({ templateName, botNumber, onBack }) {
  const styles = useTemplateSx();
  const form = useTemplateForm({ templateName, botNumber });

  const bodyObj = form.formData?.components?.find((c) => c.type === 'BODY');
  const bodyText = bodyObj?.text || '';
  const bodyVariables = bodyObj?.example?.body_text?.[0] || [];
  const footerText =
    form.formData?.components?.find((c) => c.type === 'FOOTER')?.text || '';

  const headerFormat = form.selectedHeaderFormat;
  const showHeaderInput =
    form.getHeaderType('IMAGE') ||
    form.getHeaderType('VIDEO') ||
    form.getHeaderType('DOCUMENT') ||
    form.getHeaderType('TEXT');

  const pageTitle = useMemo(
    () => (form.isEditMode ? `Edit ${templateName}` : 'Create Template'),
    [form.isEditMode, templateName]
  );

  const isTrustSignalBot = form.filterObj?.botNumberObj?.source === 'trustsignal';

  return (
    <Box sx={styles.formPageRoot}>
      {form.isCloneMode ? (
        <Alert severity="info" sx={{ mb: 2 }}>
          Cloned from <strong>{form.cloneSourceName}</strong> — submit to create a new template on Meta.
        </Alert>
      ) : null}

      <Box sx={styles.formHeader}>
        <Box>
          <Typography sx={styles.formTitle}>{pageTitle}</Typography>
          <Typography sx={styles.formSubtitle}>
            {form.isEditMode
              ? 'Update template content and settings'
              : 'Define message content for WhatsApp approval'}
          </Typography>
        </Box>
        {onBack ? (
          <Button
            variant="outline"
            size="sm"
            icon={<ArrowBackIcon sx={{ fontSize: 16 }} />}
            onClick={onBack}
            sx={styles.formBackButton}
          >
            Back
          </Button>
        ) : null}
      </Box>

      {form.isEditMode && form.filterObj?.botNumberObj?.app_name ? (
        <Box sx={styles.botContextBar}>
          <Box>
            <Typography sx={styles.botContextLabel}>
              {form.filterObj.botNumberObj.app_name}
            </Typography>
            <Typography sx={styles.botContextMeta}>
              {form.filterObj.botNumberObj.wa_phone_number}
            </Typography>
          </Box>
        </Box>
      ) : null}

      <TemplateRejectionAlert template={form.formData} />

      <Grid container spacing={2.5}>
        <Grid item xs={12} lg={8}>
          <FormSection title="Template details">
            {!form.isEditMode ? (
              <OrganisationBotFields
                isSuperuser={form.isSuperuser}
                organisation={form.filterObj?.organisationObj ?? null}
                botNumber={form.filterObj?.botNumberObj ?? null}
                onOrganisationChange={form.handleOrganisationChange}
                onBotChange={form.handleBotChange}
                errors={form.contextErrors}
                botDisabled={form.botOptionsCount === 1}
                sx={styles.formFieldSpacing}
              />
            ) : null}

            <Box sx={styles.formFieldSpacing}>
              <Typography sx={styles.formLabel}>
                <RequiredLabel>Category</RequiredLabel>
              </Typography>
              <OptionChipGroup
                options={CategoryOptions}
                value={form.formData?.category || 'UTILITY'}
                onChange={(value) => form.setFormData({ ...form.formData, category: value })}
                disabled={form.isEditMode}
              />
            </Box>

            <Field
              label={<RequiredLabel>Template name</RequiredLabel>}
              value={form.formData?.name || ''}
              onChange={(e) => form.handleChangeName(e.target.value)}
              disabled={form.isEditMode}
              maxLength={512}
              error={Boolean(form.formErrors?.name)}
              errorMessage={form.formErrors?.name}
              helperText={
                form.isCheckingTemplateName
                  ? 'Checking name availability...'
                  : `${form.formData?.name?.length || 0}/512 characters`
              }
              sx={styles.formFieldSpacing}
            />

            <Field
              label="Description (Optional)"
              value={form.formData?.description || ''}
              onChange={(e) => form.handleChangeDescription(e.target.value)}
              multiline
              rows={2}
              sx={styles.formFieldSpacing}
            />

            <Grid container spacing={2} sx={styles.formFieldSpacing}>
              <Grid item xs={12} sm={6}>
                <TemplateSearchableSelect
                  label={<RequiredLabel>Language</RequiredLabel>}
                  options={LanguageList}
                  value={LanguageList.find((obj) => obj.value === form.formData?.language) || null}
                  onChange={(selected) => {
                    form.setFormData({
                      ...form.formData,
                      language: selected?.value || '',
                    });
                    form.clearFieldError('language');
                  }}
                  disabled={form.isEditMode}
                  error={Boolean(form.formErrors?.language)}
                  errorMessage={form.formErrors?.language}
                  sx={{ mb: 0 }}
                />
              </Grid>
              <Grid
                item
                xs={12}
                sm={6}
                sx={(theme) => ({
                  mt: {
                    xs: `${theme.customTokens.spacing.size27 - 10}px`,
                    sm: `${theme.customTokens.spacing.size27 - 1}px`,
                  },
                })}
              >
                <TemplateTagsSelect
                  companyId={form.companyId}
                  value={form.selectedTags}
                  onChange={form.setSelectedTags}
                  sx={{
                    '& .MuiInputLabel-root:not(.MuiInputLabel-shrink)': {
                      top: '2px',
                    },
                    '& .MuiInputLabel-root.MuiInputLabel-shrink': {
                        top: '-12px',
                      },
                    '& .MuiInputLabel-root.MuiInputLabel-root':{
                      color:'#0f172a',
                      fontWeight:typography.fontWeightBold
                    },
                    '& .MuiAutocomplete-tag':{
                      margin:'0px'
                    }
                  }}
                />
              </Grid>
            </Grid>
          </FormSection>

          <FormSection title="Header" description="Optional media or text header">
            <OptionChipGroup
              variant="card"
              options={HEADER_TYPE_OPTIONS}
              value={headerFormat}
              onChange={(value) => {
                const selected = HeaderOptions.find((o) => o.value === value);
                form.onSelectHeaderType(selected);
              }}
            />

            {showHeaderInput ? (
              <Box sx={{ mt: 2 }}>
                {form.getHeaderType('TEXT') ? (
                  <Field
                    label="Header text"
                    value={form.getHeaderText()}
                    onChange={(e) => form.handleChangeHeaderText(e.target.value)}
                    error={Boolean(form.formErrors?.headerText)}
                    errorMessage={form.formErrors?.headerText}
                  />
                ) : (
                  <>
                    <FileInput
                      onFileSelect={form.onFileSelect}
                      onFileRemove={form.onFileRemove}
                      existingFileUrl={form.existingFileUrl}
                      file={form.file}
                      setFile={form.setFile}
                      accept={getHeaderAcceptValue(headerFormat)}
                    />
                    <Typography sx={{ mt: 1, fontSize: '0.75rem', color: 'text.secondary' }}>
                      {form.getHeaderType('IMAGE') && 'Allowed: JPG, PNG • Max 5 MB'}
                      {form.getHeaderType('VIDEO') && 'Allowed: MP4, 3GP • Max 16 MB'}
                      {form.getHeaderType('DOCUMENT') &&
                        'Allowed: PDF, DOC, DOCX • Max 95 MB'}
                    </Typography>
                  </>
                )}
              </Box>
            ) : null}
          </FormSection>

          <FormSection title="Body">
            <Typography sx={{ ...styles.formLabel, mb: 1 }}>
              <RequiredLabel>Body</RequiredLabel>
            </Typography>
            <WhatsAppBodyEditor
              ref={form.bodyEditorRef}
              value={bodyText}
              onChange={form.handleChangeBodyData}
              error={Boolean(form.formErrors?.body)}
              helperText={form.formErrors?.body || null}
              placeholder="Write your message here..."
            />

            <Box sx={{ ...styles.formBodyFooter, mt: 1.5, justifyContent: 'flex-end' }}>
              <Button
                variant="secondary"
                size="sm"
                icon={<AddIcon sx={{ fontSize: 16 }} />}
                onClick={form.addBodyVariables}
              >
                Add Variables
              </Button>
            </Box>

            {bodyVariables.length > 0 ? (
              <Box sx={styles.formVariablesBlock}>
                <Typography sx={styles.formVariablesTitle}>Body Variables</Typography>
                {bodyVariables.map((item, index) => (
                  <Grid container spacing={1.5} alignItems="flex-start" key={index} mb={1.5}>
                    <Grid item xs={12} sm={1}>
                      <Typography sx={styles.formVariableIndex}>{`{{${index + 1}}}`}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={5}>
                      <Field
                        label={`Param ${index + 1}`}
                        value={item || ''}
                        onChange={(e) => form.handleChangeBodyVariables(e.target.value, index)}
                        error={Boolean(form.formErrors?.[`bodyVariable_${index}`])}
                        errorMessage={form.formErrors?.[`bodyVariable_${index}`]}
                      />
                    </Grid>
                    <Grid item xs={12} sm={5}>
                      <Field
                        label={`Example ${index + 1}`}
                        value={form.formData?.param?.[index] || ''}
                        onChange={(e) => form.handleChangeBodyParams(e.target.value, index)}
                        error={Boolean(form.formErrors?.[`bodyParams_${index}`])}
                        errorMessage={form.formErrors?.[`bodyParams_${index}`]}
                      />
                    </Grid>
                    {bodyVariables.length - 1 === index ? (
                      <Grid item xs={12} sm={1}>
                        <IconButton
                          onClick={() => form.removeBodyVariables(index)}
                          size="small"
                          sx={{ mt: 2.5 }}
                        >
                          <CloseIcon fontSize="small" color="error" />
                        </IconButton>
                      </Grid>
                    ) : null}
                  </Grid>
                ))}
              </Box>
            ) : null}
          </FormSection>

          <FormSection title="Footer" description="Optional short footer text">
            <Field
              label="Footer"
              maxLength={50}
              value={footerText}
              onChange={(e) => form.handleChangeFooterData(e.target.value)}
              helperText={`${footerText.length}/50 characters`}
            />
          </FormSection>

          <Box sx={styles.formCallingCard}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
              <Box sx={styles.formCallingIconWrap}>
                <PhoneOutlinedIcon sx={{ fontSize: 20 }} />
              </Box>
              <Box>
                <Typography sx={styles.formCallingTitle}>Is Calling</Typography>
                <Typography sx={styles.formCallingDescription}>
                  Enable if this template initiates a call
                </Typography>
              </Box>
            </Box>
            <Switch
              size="small"
              checked={form.formData?.is_calling || false}
              onChange={(e) =>
                form.setFormData({ ...form.formData, is_calling: e.target.checked })
              }
            />
          </Box>

          {form.formData?.is_calling ? (
            <FormSection title="Calling settings">
              <Field
                label="Lead List id"
                value={form.formData?.lead_list_id || ''}
                onChange={(e) =>
                  form.setFormData({ ...form.formData, lead_list_id: e.target.value })
                }
                maxLength={50}
              />
            </FormSection>
          ) : null}

          <TemplateButtonInputs />

          {isTrustSignalBot ? (
            <TemplateClickTrackingField
              value={form.formData?.shortlink ?? '1'}
              onChange={(shortlink) =>
                form.setFormData({ ...form.formData, shortlink })
              }
              sx={{ ...styles.formFieldSpacing, mt: 2 }}
            />
          ) : null}

          <Box sx={styles.formActionsBar}>
            {onBack ? (
              <Button variant="outline" size="md" onClick={onBack}>
                Cancel
              </Button>
            ) : null}
            <Button
              variant="primary"
              size="md"
              loading={form.isSubmitting || form.isCheckingTemplateName}
              disabled={form.isSubmitting || form.isCheckingTemplateName}
              icon={<SaveOutlinedIcon sx={{ fontSize: 18 }} />}
              onClick={form.submit}
            >
              Save Template
            </Button>
          </Box>
        </Grid>

        <Grid item xs={12} lg={4}>
          <Box sx={styles.formPreviewSticky}>
            <TemplateMessagePreview
              file={form.file}
              existingFileUrl={form.existingFileUrl}
            />
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
}
